/**
 * Thread Sidebar - Slack-like thread view
 * Shows thread messages in a right sidebar
 */

import React, { useState, useEffect, useRef } from 'react';
import { Drawer, Button, Spin, Empty } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../config/api';
import { useChatWebSocket } from '../../contexts/ChatWebSocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { MessageItem } from './MessageItem';
import { MessageInput } from './MessageInput';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import './ThreadSidebar.css';

dayjs.extend(relativeTime);

interface ThreadMessage {
  id: string;
  channelId: string;
  userId: string;
  text: string;
  contentType: string;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentSize?: number;
  threadRootId?: string;
  replyTo?: string;
  isEdited?: boolean;
  isPinned?: boolean;
  createdAt: string;
  updatedAt?: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface ThreadSidebarProps {
  channelId: string;
  rootMessageId: string | null;
  onClose: () => void;
}

export const ThreadSidebar: React.FC<ThreadSidebarProps> = ({
  channelId,
  rootMessageId,
  onClose,
}) => {
  const { on: socketOn, presence } = useChatWebSocket();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);

  // Fetch thread messages
  const { data: threadData, isLoading } = useQuery(
    ['thread-messages', channelId, rootMessageId],
    () => api.get(`/api/chat/channels/${channelId}/messages/${rootMessageId}/thread`).then(res => res.data),
    {
      enabled: !!channelId && !!rootMessageId,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (threadData) {
      setMessages(threadData.messages || []);
    }
  }, [threadData]);

  // Listen for new thread messages via WebSocket
  useEffect(() => {
    if (!channelId || !rootMessageId) return;

    const unsubscribe = socketOn('message:new', (newMessage: ThreadMessage) => {
      if (newMessage.channelId === channelId && newMessage.threadRootId === rootMessageId) {
        setMessages(prev => {
          if (prev.some(m => m.id === newMessage.id)) return prev;
          return [...prev, newMessage];
        });
        // Scroll to bottom
        setTimeout(() => {
          const container = document.querySelector('.thread-messages-container');
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        }, 100);
      }
    });

    return unsubscribe;
  }, [channelId, rootMessageId, socketOn]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        const container = document.querySelector('.thread-messages-container');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 100);
    }
  }, [messages]);

  const rootMessage = threadData?.rootMessage;

  if (!rootMessageId) {
    return null;
  }

  return (
    <Drawer
      title={
        <div className="thread-sidebar-header">
          <span className="thread-sidebar-title">Thread</span>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onClose}
            className="thread-sidebar-close"
          />
        </div>
      }
      placement="right"
      closable={false}
      open={!!rootMessageId}
      onClose={onClose}
      width={400}
      className="thread-sidebar-drawer"
      mask={false}
    >
      {isLoading ? (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : !rootMessage ? (
        <Empty description="Thread not found" />
      ) : (
        <div className="thread-sidebar-content">
          {/* Root Message */}
          <div className="thread-root-message">
            <MessageItem
              message={{
                ...rootMessage,
                threadCount: messages.length,
                isPinned: rootMessage.isPinned || false,
                isEdited: rootMessage.isEdited || false,
              }}
              showAvatar={true}
              userPresence={presence[rootMessage.userId]}
              onReply={() => {}}
              onEdit={() => {}}
              onDelete={() => {}}
              onPin={() => {}}
              onReaction={() => {}}
            />
          </div>

          {/* Thread Replies Count */}
          <div className="thread-replies-header">
            {messages.length} {messages.length === 1 ? 'reply' : 'replies'}
          </div>

          {/* Thread Messages */}
          <div className="thread-messages-container">
            {messages.length === 0 ? (
              <Empty 
                description="No replies yet" 
                style={{ marginTop: 40 }}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <div className="thread-messages-list">
                {messages.map((message, index) => {
                  const previousMessage = index > 0 ? messages[index - 1] : null;
                  const showAvatar = !previousMessage || previousMessage.userId !== message.userId;
                  const userPresence = presence[message.userId];

                  return (
                    <div key={message.id} className="thread-message-item">
                      <MessageItem
                        message={{
                          ...message,
                          threadCount: 0, // Thread messages don't have nested threads
                        }}
                        showAvatar={showAvatar}
                        userPresence={userPresence}
                        onReply={() => {}}
                        onEdit={() => {}}
                        onDelete={() => {}}
                        onPin={() => {}}
                        onReaction={() => {}}
                        onOpenThread={() => {}}
                      />
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Thread Input */}
          <div className="thread-input-container">
            <MessageInput
              channelId={channelId}
              replyTo={rootMessageId}
              onCancelReply={() => {}}
              onMessageSent={() => {
                // Refetch thread messages and main channel messages after sending
                setTimeout(() => {
                  queryClient.invalidateQueries(['thread-messages', channelId, rootMessageId]);
                  queryClient.invalidateQueries(['chat-messages', channelId]);
                }, 500);
              }}
            />
          </div>
        </div>
      )}
    </Drawer>
  );
};

